import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import CheckIcon from '@mui/icons-material/Check';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  GlobalStyles,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  type SvgIconProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useId, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { useActiveClientIds, useActiveCompany } from '../client/ActiveCompanyContext';
import { ClientWorkProvider, useClientWork } from '../client/ClientWorkContext';
import { InstallAppCard } from '../components/InstallAppCard';
import { StaffActingClientBar } from '../components/StaffActingClientBar';
import { writeChosenLocale, type AppLocale } from '../lib/accountLocale';
import { PendingInvitationBanner } from '../pages/client/onboarding/PendingInvitationBanner';
import { signOutCurrent } from '../services/firebase';
import { useClientConversations, useClientPersonalUnreadCount } from '../services/useMessaging';
import { color } from '../theme/foundation';

import { LanguageSwitcherMui } from './LanguageSwitcherMui';
import { NotificationBell } from './NotificationBell';
import { PageHeaderProvider, useShellHeading } from './pageHeaderContext';
import {
  shellActionsSpacing,
  shellDrawerPaperSx,
  shellHeaderSx,
  shellMainSx,
  shellRootSx,
  shellTitleSx,
} from './shellStyles';
import { BADGE_MAX, unreadLabel } from './unreadLabel';

/**
 * UX 2.3 — the client's navigation (F2, UI-alus §1 "Navigatsioon", §3), on the
 * WorkerShell pattern of stage 1A.
 *
 *  - < 600 px: bottom bar Töölaud · Tellimused · Tunnid · Sõnumid · Veel;
 *    "Veel" (and the header menu button) open the drawer with everything —
 *    Objektid, Arved and Ettevõte included.
 *  - 600–1199 px: a rail — icon over a one-word label — with every destination.
 *  - ≥ 1200 px: the permanent side menu.
 *
 * Exactly one "Menüü" landmark exists at any width: only the drawer or rail
 * for the current width is mounted. The shell label is the page's h1 unless a
 * PageHeader owns it. Badges are the counters of the ONE `getMyClientWork`
 * read (ClientWorkContext) — Tellimused = orders waiting on me, Tunnid = shifts
 * to approve, Arved (under Veel) = overdue — and Sõnumid the live company inbox.
 *
 * "Ootab sind" (the old "Objektid ja tegevused" page) is part of Töölaud now;
 * its route stays until the integrator redirects it.
 */

const DRAWER_WIDTH = 248;
const RAIL_WIDTH = 88;

type NavKey =
  | 'dashboard'
  | 'orders'
  | 'delivered'
  | 'messages'
  | 'objects'
  | 'invoices'
  | 'company';

const NAV_ITEMS: ReadonlyArray<{ to: string; key: NavKey; Icon: ComponentType<SvgIconProps> }> = [
  { to: '/client', key: 'dashboard', Icon: DashboardOutlinedIcon },
  { to: '/client/orders', key: 'orders', Icon: WorkOutlineOutlinedIcon },
  { to: '/client/delivered', key: 'delivered', Icon: AssignmentTurnedInOutlinedIcon },
  { to: '/client/messages', key: 'messages', Icon: ChatBubbleOutlineOutlinedIcon },
  { to: '/client/objects', key: 'objects', Icon: LocationOnOutlinedIcon },
  { to: '/client/invoices', key: 'invoices', Icon: ReceiptLongOutlinedIcon },
  { to: '/client/company', key: 'company', Icon: BusinessOutlinedIcon },
];

/** On the phone's bottom bar (F2); the rest are under "Veel". */
const BAR_KEYS: ReadonlyArray<NavKey> = ['dashboard', 'orders', 'delivered', 'messages'];

/** The shell label — the page's h1 unless a PageHeader owns it — names the menu item. */
const TITLE_KEY: Record<NavKey | 'myWork', string> = {
  dashboard: 'client:pageTitles.dashboard',
  orders: 'client:pageTitles.orders',
  delivered: 'client:nav.delivered',
  messages: 'client:pageTitles.messages',
  objects: 'client:nav.objects',
  invoices: 'client:pageTitles.invoices',
  company: 'client:pageTitles.company',
  myWork: 'client:pageTitles.myWork',
};

/** Language names are endonyms — the same in every locale. */
const LOCALES: ReadonlyArray<{ code: AppLocale; label: string }> = [
  { code: 'et', label: 'Eesti' },
  { code: 'en', label: 'English' },
  { code: 'ru', label: 'Русский' },
  { code: 'fi', label: 'Suomi' },
];

function getPageKey(pathname: string): NavKey | 'myWork' {
  if (pathname === '/client' || pathname === '/client/') return 'dashboard';
  if (pathname.startsWith('/client/my-work')) return 'myWork';
  const match = NAV_ITEMS.find((item) => item.to !== '/client' && pathname.startsWith(item.to));
  return match?.key ?? 'dashboard';
}

const srOnly = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/**
 * The page title in the header: the page's `h1` unless a `PageHeader` on the
 * route owns it (UX 1.0a contract, pageHeaderContext.tsx). Never an `h2`.
 */
function ShellTitle({ label }: { label: string }) {
  const heading = useShellHeading(label);
  return (
    <Typography
      variant="h2"
      noWrap
      {...heading.titleProps}
      sx={[shellTitleSx, heading.titleProps.sx] as SxProps<Theme>}
    >
      {label}
    </Typography>
  );
}

function barButtonSx(active: boolean): SxProps<Theme> {
  return {
    minWidth: 0,
    minHeight: 64,
    px: 0.5,
    borderRadius: 0,
    flexDirection: 'column',
    gap: 0.5,
    textTransform: 'none',
    fontSize: 12,
    lineHeight: 1.2,
    overflowWrap: 'anywhere',
    color: active ? color.brandText : 'text.secondary',
    bgcolor: active ? color.brandSoft : undefined,
  };
}

export function ClientShell({ children }: { children: ReactNode }) {
  // UX 7.1 (D11) — threads unread FOR ME, from my own read marks. The shared
  // `clientUnread` was cleared for the whole team by whoever opened a thread
  // first, so a colleague's click hid a message I had never seen. Live.
  const clientIds = useActiveClientIds();
  const { user } = useAuth();
  const { conversations } = useClientConversations(clientIds);
  const messagesUnread = useClientPersonalUnreadCount(conversations, user?.uid);
  return (
    <ClientWorkProvider messagesUnread={messagesUnread}>
      <ClientShellFrame>{children}</ClientShellFrame>
    </ClientWorkProvider>
  );
}

function ClientShellFrame({ children }: { children: ReactNode }) {
  const { conflict: companyConflict } = useActiveCompany();
  const { t } = useTranslation(['client', 'common']);
  const location = useLocation();
  const { attention } = useClientWork();
  const counts = attention.counts;
  // `noSsr`: read the width on the first render, so the menu for the wrong
  // width (and its nav landmark) never mounts for a frame.
  const tablet = useMediaQuery('(min-width:600px)', { noSsr: true });
  const wide = useMediaQuery('(min-width:1200px)', { noSsr: true });
  const phone = !tablet;
  const [mobileOpen, setMobileOpen] = useState(false);
  const drawerId = useId();

  useEffect(() => {
    if (!location.hash) window.scrollTo?.(0, 0);
  }, [location.pathname, location.hash]);

  // The drawer belongs to the phone layout; widening the window closes it.
  useEffect(() => {
    if (!phone) setMobileOpen(false);
  }, [phone]);

  const activeKey = getPageKey(location.pathname);
  const activeNav: NavKey = activeKey === 'myWork' ? 'dashboard' : activeKey;

  const label = (key: NavKey | 'more') => t(`client:nav.${key}`);

  /** The counter of each destination; 0 = no badge. */
  const countOf = (key: NavKey): number =>
    key === 'orders'
      ? counts.orders
      : key === 'delivered'
        ? counts.hours
        : key === 'messages'
          ? counts.messages
          : key === 'invoices'
            ? counts.invoices
            : 0;

  /** The icon with its badge. The number is hidden from AT; `countText` names it. */
  const navIcon = (key: NavKey, Icon: ComponentType<SvgIconProps>, small = false) => {
    const icon = <Icon fontSize={small ? 'small' : 'medium'} />;
    const n = countOf(key);
    if (n <= 0) return icon;
    return (
      <Badge
        badgeContent={n}
        max={BADGE_MAX}
        color="error"
        data-testid={`nav-badge-${key}`}
        slotProps={{ badge: { 'aria-hidden': true } as Record<string, unknown> }}
      >
        {icon}
      </Badge>
    );
  };
  const countText = (key: NavKey | 'more') => {
    const n = key === 'more' ? counts.invoices : countOf(key);
    if (n <= 0) return null;
    const which = key === 'more' ? 'invoices' : key;
    return (
      <Box component="span" sx={srOnly}>
        {` (${t(`client:nav.count.${which}`, { count: n })})`}
      </Box>
    );
  };

  const menuName = t('common:ui.shell.navLabel', { defaultValue: 'Menüü' });

  const sidebar = (
    <Box
      component="nav"
      id={drawerId}
      aria-label={menuName}
      sx={{ width: DRAWER_WIDTH, height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ px: 2.5, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src="/rm24_logo.jpg"
          alt={t('common:app.name')}
          sx={{ width: 36, height: 36, borderRadius: 1.5, objectFit: 'cover' }}
        />
        <Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.1 }}
          >
            {t('common:app.name')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('client:shell.portalLabel')}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ mx: 1 }} />
      <List sx={{ py: 1, flexGrow: 1 }}>
        {/* Each link in its own <li>: a link directly inside <ul> breaks the list (axe "list"). */}
        {NAV_ITEMS.map(({ to, key, Icon }) => (
          <ListItem key={to} disablePadding>
            <ListItemButton
              component={NavLink}
              to={to}
              // `end` on the dashboard: otherwise NavLink marks it
              // aria-current="page" on every /client/* route.
              end={to === '/client'}
              selected={activeNav === key}
              onClick={() => setMobileOpen(false)}
              sx={{ minHeight: 48 }}
            >
              <ListItemIcon>{navIcon(key, Icon, true)}</ListItemIcon>
              <ListItemText
                primary={
                  <>
                    {label(key)}
                    {countText(key)}
                  </>
                }
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: activeNav === key ? 600 : 500,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mx: 1 }} />
      <Box sx={{ p: 1 }}>
        <ListItemButton onClick={() => void signOutCurrent()} sx={{ minHeight: 48 }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('common:actions.signOut')}
            primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  /** 600–1199 px: M3 navigation rail — every destination, icon over a one-word label. */
  const rail = (
    <Box
      component="nav"
      aria-label={menuName}
      sx={{
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        width: RAIL_WIDTH,
        zIndex: (theme) => theme.zIndex.appBar,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        pt: 'calc(12px + env(safe-area-inset-top, 0px))',
        pb: 'calc(12px + env(safe-area-inset-bottom, 0px))',
        pl: 'env(safe-area-inset-left, 0px)',
        overflowY: 'auto',
      }}
    >
      <Box
        component="img"
        src="/rm24_logo.jpg"
        alt={t('common:app.name')}
        sx={{ width: 40, height: 40, borderRadius: 1.5, objectFit: 'cover', mx: 'auto', mb: 1.5 }}
      />
      <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, flexGrow: 1 }}>
        {NAV_ITEMS.map(({ to, key, Icon }) => (
          <Box component="li" key={to} sx={{ px: 0.5, py: 0.25 }}>
            <ButtonBase
              component={NavLink}
              to={to}
              end={to === '/client'}
              sx={{
                width: '100%',
                minHeight: 56,
                py: 0.75,
                borderRadius: 2,
                flexDirection: 'column',
                gap: 0.5,
                fontSize: 12,
                fontWeight: activeNav === key ? 700 : 500,
                lineHeight: 1.2,
                textAlign: 'center',
                overflowWrap: 'anywhere',
                color: activeNav === key ? color.brandText : 'text.secondary',
                bgcolor: activeNav === key ? color.brandSoft : undefined,
              }}
            >
              {navIcon(key, Icon)}
              <span>
                {label(key)}
                {countText(key)}
              </span>
            </ButtonBase>
          </Box>
        ))}
      </Box>
    </Box>
  );

  const drawerPaperSx = {
    '& .MuiDrawer-paper': {
      width: DRAWER_WIDTH,
      boxSizing: 'border-box',
      ...shellDrawerPaperSx,
    },
  } as const;

  return (
    <Box sx={shellRootSx}>
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--rm24-bottom-nav-height': 'calc(65px + env(safe-area-inset-bottom, 0px))',
            [theme.breakpoints.up('sm')]: { '--rm24-bottom-nav-height': '0px' },
          },
        })}
      />
      {/* Only the menu for the current width is mounted, so exactly one menu
          landmark exists. The slot reserves the rail's / side menu's width. */}
      <Box sx={{ width: wide ? DRAWER_WIDTH : tablet ? RAIL_WIDTH : 0, flexShrink: 0 }}>
        {wide ? (
          <Drawer variant="permanent" open sx={drawerPaperSx}>
            {sidebar}
          </Drawer>
        ) : tablet ? (
          rail
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              // Kept mounted for speed, but a CLOSED modal drawer must not take
              // part in layout: MUI only hides it with `visibility`, so its box
              // stayed and stretched the document to 540px on a 393px phone.
              '&.MuiModal-hidden': { display: 'none' },
              ...drawerPaperSx,
            }}
          >
            {sidebar}
          </Drawer>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <PageHeaderProvider>
          <Toolbar component="header" disableGutters sx={shellHeaderSx}>
            {phone && (
              <IconButton
                edge="start"
                onClick={() => setMobileOpen((v) => !v)}
                sx={{ mr: 1.5 }}
                aria-label={t('common:ui.shell.openMenu', { defaultValue: 'Ava menüü' })}
                aria-expanded={mobileOpen}
                aria-controls={drawerId}
              >
                <MenuIcon />
              </IconButton>
            )}

            <ShellTitle label={t(TITLE_KEY[activeKey])} />

            <Stack
              direction="row"
              spacing={shellActionsSpacing}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              {/* On a phone the language is in the account menu. */}
              {tablet && <LanguageSwitcherMui />}
              {wide && (
                <Tooltip title={t('client:nav.messages') ?? ''}>
                  <IconButton
                    aria-label={unreadLabel(t, t('client:nav.messages'), counts.messages)}
                    component={NavLink}
                    to="/client/messages"
                  >
                    {counts.messages > 0 ? (
                      <Badge badgeContent={counts.messages} max={BADGE_MAX} color="error">
                        <ChatBubbleOutlineOutlinedIcon />
                      </Badge>
                    ) : (
                      <ChatBubbleOutlineOutlinedIcon />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <NotificationBell />
              <AccountMenu showName={wide} />
            </Stack>
          </Toolbar>

          <Box
            component="main"
            sx={[
              ...(Array.isArray(shellMainSx) ? shellMainSx : [shellMainSx]),
              {
                pb: { xs: 'calc(88px + env(safe-area-inset-bottom, 0px))', sm: 3 },
                '& .MuiSnackbar-anchorOriginBottomCenter': {
                  bottom: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px))', sm: 24 },
                },
              },
            ]}
          >
            <StaffActingClientBar />
            <PendingInvitationBanner />
            {/* §2.1 — an account linked to two companies has no active one, so
              every list below is empty. Said once, here. */}
            {companyConflict && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t('client:company.conflict', {
                  defaultValue:
                    'See konto on seotud mitme ettevõttega. Ühe ettevõtte mudelis ei valita ' +
                    'neist automaatselt — võta ühendust RM24-ga, et seos korda teha.',
                })}
              </Alert>
            )}
            {children}
            {/* 2.3: the install offer never sits on top of the page's work —
                after it, and closable. The component itself is unchanged. */}
            <Box sx={{ mt: 2 }}>
              <InstallAppCard />
            </Box>
          </Box>
        </PageHeaderProvider>
      </Box>
      {phone && (
        <Box
          component="nav"
          aria-label={t('client:nav.bottomBar')}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            pb: 'env(safe-area-inset-bottom, 0px)',
            pl: 'env(safe-area-inset-left, 0px)',
            pr: 'env(safe-area-inset-right, 0px)',
          }}
        >
          {NAV_ITEMS.filter(({ key }) => BAR_KEYS.includes(key)).map(({ to, key, Icon }) => (
            <Button
              key={key}
              component={NavLink}
              to={to}
              end={to === '/client'}
              sx={barButtonSx(activeNav === key)}
            >
              {navIcon(key, Icon)}
              <span>
                {label(key)}
                {countText(key)}
              </span>
            </Button>
          ))}
          <Button
            onClick={() => setMobileOpen(true)}
            aria-expanded={mobileOpen}
            aria-controls={drawerId}
            sx={barButtonSx(!BAR_KEYS.includes(activeNav))}
          >
            {counts.invoices > 0 ? (
              <Badge
                badgeContent={counts.invoices}
                max={BADGE_MAX}
                color="error"
                data-testid="nav-badge-more"
                slotProps={{ badge: { 'aria-hidden': true } as Record<string, unknown> }}
              >
                <MoreHorizIcon />
              </Badge>
            ) : (
              <MoreHorizIcon />
            )}
            <span>
              {label('more')}
              {countText('more')}
            </span>
          </Button>
        </Box>
      )}
    </Box>
  );
}

/**
 * The avatar's menu: Keel · Teavitused · Logi välja (2.3). The language list
 * is here so a phone header holds only the menu button, title, bell and this.
 * "Teavitused" is the optional-push switch the bell also carries; the push
 * service is loaded only when the menu opens.
 */
function AccountMenu({ showName }: { showName: boolean }) {
  const { t, i18n } = useTranslation(['client', 'common']);
  const { user } = useAuth();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [optionalOn, setOptionalOn] = useState<boolean | null>(null);
  const menuId = useId();
  const open = Boolean(anchor);
  const uid = user?.uid;

  useEffect(() => {
    if (!open || !uid) return;
    let unsub: (() => void) | undefined;
    let alive = true;
    void import('../services/push')
      .then((push) => {
        if (alive) unsub = push.subscribeOptionalPushEnabled(uid, setOptionalOn);
      })
      .catch(() => setOptionalOn(null));
    return () => {
      alive = false;
      unsub?.();
    };
  }, [open, uid]);

  const toggleOptional = async () => {
    if (optionalOn === null) return;
    const next = !optionalOn;
    setOptionalOn(next);
    try {
      const push = await import('../services/push');
      await push.setOptionalPushEnabled(next);
    } catch {
      setOptionalOn(!next);
    }
  };

  const current = (i18n?.resolvedLanguage ?? 'et') as AppLocale;
  const changeLanguage = (code: AppLocale) => {
    void i18n?.changeLanguage(code);
    writeChosenLocale(uid ?? null, code);
    setAnchor(null);
  };

  const displayName = user?.displayName ?? user?.email ?? '';
  const initials = displayName
    .split(/[\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        sx={{ borderRadius: 999, pl: showName ? 1 : 0, gap: 1.25, minHeight: 48, minWidth: 48 }}
      >
        {/* F1: the orange fill with dark initials (6.93:1). */}
        <Avatar
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            bgcolor: color.brandFill,
            color: color.onBrandFill,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {initials || 'C'}
        </Avatar>
        <Box component="span" sx={srOnly}>
          {t('client:nav.account')}
        </Box>
        {showName && (
          <Box component="span" sx={{ lineHeight: 1.2, textAlign: 'left' }} aria-hidden>
            <Typography
              component="span"
              variant="body2"
              sx={{ display: 'block', fontWeight: 600, color: 'text.primary' }}
            >
              {displayName}
            </Typography>
            <Typography component="span" variant="caption" color="text.secondary">
              {t('client:header.clientRole')}
            </Typography>
          </Box>
        )}
      </ButtonBase>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, maxWidth: '90vw' } } }}
      >
        {/* A visual caption only: a menu may own nothing but items and
            separators, and the items' endonyms already say what they are. */}
        <Box
          component="li"
          role="none"
          aria-hidden
          sx={{ px: 2, py: 0.75, typography: 'caption', color: 'text.secondary' }}
        >
          {t('client:nav.language')}
        </Box>
        {LOCALES.map(({ code, label }) => (
          <MenuItem
            key={code}
            lang={code}
            role="menuitemradio"
            aria-checked={current === code}
            onClick={() => changeLanguage(code)}
          >
            <ListItemIcon>{current === code ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          role="menuitemcheckbox"
          aria-checked={optionalOn === true}
          disabled={optionalOn === null}
          onClick={() => void toggleOptional()}
        >
          <ListItemIcon>
            <NotificationsNoneOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t('client:nav.notifications')}
            secondary={t('common:push.optional')}
          />
          {/* State is the item's aria-checked; the tick shows it. A real
              switch here would be a control nested in a control. */}
          {optionalOn && <CheckIcon fontSize="small" sx={{ ml: 1 }} aria-hidden />}
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void signOutCurrent();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('common:actions.signOut')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
